import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { BookOpen, ArrowRight, Clock } from "lucide-react";

export default function CityGuidesSection() {
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  const { withCovers, withoutCovers } = useMemo(() => {
    const wc = [], woc = [];
    for (const c of cities) {
      const has = (Array.isArray(c.guide_covers) && c.guide_covers.length > 0) || !!c.guide_cover_url;
      if (has) wc.push(c); else woc.push(c);
    }
    return { withCovers: wc, withoutCovers: woc };
  }, [cities]);

  if (!cities.length) return null;

  return (
    <section className="bg-gradient-to-b from-amber-50/50 to-white py-14 sm:py-20 border-y border-amber-100" data-testid="city-guides-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-8">
          <div>
            <p className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-amber-900 bg-amber-100 px-2.5 py-1 rounded-full">
              <BookOpen size={12}/> Edições impressas
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3 max-w-2xl leading-tight">
              Confira as capas das edições dos guias impressos
            </h2>
            <p className="text-stone-600 mt-2 text-sm max-w-xl">
              Cada cidade tem várias edições publicadas — navegue pelo arquivo visual das capas.
            </p>
          </div>
        </div>

        {/* DESTAQUE: cidades com capas publicadas */}
        {withCovers.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="city-guides-published">
            {withCovers.map((c) => {
              const covers = Array.isArray(c.guide_covers) ? c.guide_covers : [];
              const firstCover = covers[0]?.url || c.guide_cover_url;
              const editionsCount = covers.length || (c.guide_cover_url ? 1 : 0);
              return (
                <Link
                  key={c.slug}
                  to={`/guia/${c.slug}`}
                  className="group relative rounded-3xl overflow-hidden border bg-white transition-all border-stone-200 hover:shadow-2xl hover:-translate-y-1 hover:border-amber-300 cursor-pointer"
                  data-testid={`city-guide-card-${c.slug}`}
                >
                  <div className="aspect-[3/4] bg-gradient-to-br from-amber-100 via-stone-100 to-blue-100 overflow-hidden relative">
                    <img src={firstCover} alt={`Guia ${c.name}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                    <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-black/40 via-black/10 to-transparent pointer-events-none"/>
                    {editionsCount > 1 && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold uppercase tracking-wider shadow-lg">
                        {editionsCount} edições
                      </div>
                    )}
                    <div className="absolute inset-0 bg-blue-950/0 group-hover:bg-blue-950/40 transition-colors flex items-end justify-center pb-6">
                      <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 text-amber-950 font-bold text-sm shadow-xl">
                        Ver capas <ArrowRight size={14}/>
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-stone-500">{c.state}</p>
                    <h3 className="font-display text-lg font-bold text-stone-900 mt-0.5">{c.guide_title || `Guia ${c.name}`}</h3>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Em breve: menor, 2 cols mobile, 4 cols desktop */}
        {withoutCovers.length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-10 mb-4">
              <Clock size={13} className="text-stone-400"/>
              <p className="text-[11px] font-bold uppercase tracking-wider text-stone-500">Em breve</p>
              <div className="flex-1 h-px bg-stone-200"/>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="city-guides-coming">
              {withoutCovers.map((c) => (
                <div
                  key={c.slug}
                  className="relative rounded-2xl overflow-hidden border border-stone-200 bg-white opacity-90"
                  data-testid={`city-guide-card-${c.slug}`}
                  aria-disabled
                >
                  <div className="aspect-[3/4] bg-gradient-to-br from-amber-50 via-stone-100 to-blue-50 overflow-hidden relative">
                    {c.cover_image ? (
                      <img src={c.cover_image} alt={c.name} loading="lazy" className="w-full h-full object-cover blur-[2px] grayscale"/>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-400">
                        <BookOpen size={32}/>
                      </div>
                    )}
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-stone-900/85 backdrop-blur text-white text-[9px] font-bold uppercase tracking-wider" data-testid={`city-guide-coming-${c.slug}`}>
                      <Clock size={9}/> Em breve
                    </div>
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-stone-500">{c.state}</p>
                    <h3 className="font-display text-xs font-bold text-stone-900 mt-0.5 leading-tight line-clamp-1">{c.guide_title || `Guia ${c.name}`}</h3>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
